import { useState, useEffect, useRef } from 'react';
import { api } from '@/api/client';
import type {
  EmailKind,
  ReviewCheckModalProjectPayload,
  PreArchiveUploadModalProjectPayload,
  MountingCompleteModalProjectPayload,
  CorrectionsAppliedModalProjectPayload,
  ProjectReviewedModalProjectPayload,
} from '@/api/client';

const MIN_LOADER_MS = 1000;

export interface EmailDraftRequest {
  kind: EmailKind;
  project_id: string;
  recipient_person_ids: string[];
  review_check_type?: string | null;
  review_deadline?: string | null;
  modal_project:
    | ReviewCheckModalProjectPayload
    | PreArchiveUploadModalProjectPayload
    | MountingCompleteModalProjectPayload
    | CorrectionsAppliedModalProjectPayload
    | ProjectReviewedModalProjectPayload;
}

export interface UseEmailDraftReturn {
  previewHtml: string;
  draftLoading: boolean;
}

/**
 * Хук для управления черновиком письма.
 * Изолирует логику: поколения запросов, отмена устаревших ответов, минимальный лоадер.
 *
 * @param open - открыта ли модалка
 * @param draftDepsKey - строка-ключ; при изменении запускается новый запрос
 * @param request - тело запроса (должно быть стабилизировано через debounce на стороне вызывающего)
 * @param onError - колбэк при ошибке сети/сервера
 */
export function useEmailDraft({
  open,
  draftDepsKey,
  request,
  onError,
}: {
  open: boolean;
  draftDepsKey: string;
  request: EmailDraftRequest;
  onError: (message: string) => void;
}): UseEmailDraftReturn {
  const [previewHtml, setPreviewHtml] = useState('');
  const [draftLoading, setDraftLoading] = useState(false);
  const genRef = useRef(0);

  useEffect(() => {
    if (!open) {
      genRef.current += 1;
      setDraftLoading(false);
      return;
    }

    const gen = ++genRef.current;
    setDraftLoading(true);
    let cancelled = false;

    (async () => {
      const started = performance.now();
      let htmlToSet: string | null = null;

      try {
        const res = await api.emails.draft(request);
        if (cancelled || gen !== genRef.current) return;
        htmlToSet = res.html_body;
      } catch (e) {
        if (cancelled || gen !== genRef.current) return;
        onError((e as Error).message);
      }

      if (cancelled || gen !== genRef.current) return;

      const remaining = Math.max(0, MIN_LOADER_MS - (performance.now() - started));
      if (remaining > 0) {
        await new Promise<void>((r) => setTimeout(r, remaining));
      }

      if (cancelled || gen !== genRef.current) return;
      if (htmlToSet !== null) setPreviewHtml(htmlToSet);
      setDraftLoading(false);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draftDepsKey]);

  return { previewHtml, draftLoading };
}
